import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilRuler } from 'lucide-react';
import { FloorPlan, FloorLegend, ViewSwitch, useFloorView } from '../floor/FloorPlan';
import { UnitPanel } from './UnitPanel';
import { ModernCheckoutDialog } from '../ModernCheckout';
import { OrderTaker } from '../staff/OrderTaker';

// Plano del POS en Moderno: mismos datos y acciones que el POS clásico (los recibe del panel)
export default function ModernPOS({ spaces, units, decor, now, user, canSeeAmounts, details, loadDetails, onOpenUnit, opening,
  onAddItem, onStatusChange, onPrint, checkout, initialUnitId }) {
  const navigate = useNavigate();
  const [spaceId, setSpaceId] = useState('all');
  const [selectedId, setSelectedId] = useState(initialUnitId || null);
  const [ordering, setOrdering] = useState(false);
  const { view, setView, many } = useFloorView(units.length);
  const selected = units.find(u => u.unit_id === selectedId) || null;
  const sid = selected?.session_id;
  useEffect(() => { if (selected) loadDetails(selected); }, [selectedId, sid, selected?.total, selected?.state, selected?.ready_count]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownUnits = useMemo(() => [...units, ...decor], [units, decor]);
  const canCharge = ['business_admin', 'cashier', 'super_admin'].includes(user?.role);
  const sendKitchen = async () => {
    await Promise.all((details?.orders || []).filter(o => o.status === 'Nuevo').map(o => onStatusChange(o.order_id, 'Confirmado')));
    loadDetails(selected);
  };
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="modern-pos">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#111827] p-2 pl-3" data-surface="dark">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Áreas">
          {[{ space_id: 'all', name: 'Todas' }, ...spaces].map(s => (
            <button key={s.space_id} role="tab" aria-selected={spaceId === s.space_id} onClick={() => setSpaceId(s.space_id)}
              data-testid={`area-tab-${s.space_id}`}
              className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${spaceId === s.space_id ? 'bg-white text-[#111827]' : 'bg-white/10 text-white hover:bg-white/20'}`}>{s.name}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {user?.role !== 'waiter' && user?.role !== 'cashier' && (
            <button onClick={() => navigate('/layout-editor')} className="h-9 px-3 rounded-lg text-sm font-semibold text-white hover:bg-white/10 inline-flex items-center gap-1.5" data-testid="edit-floor-btn">
              <PencilRuler className="w-4 h-4" />Editar plano
            </button>
          )}
          <ViewSwitch view={view} setView={setView} many={many} />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="flex-1 min-w-0 w-full space-y-3">
          <FloorPlan units={shownUnits} spaces={spaces} spaceId={spaceId} view={view} now={now} selectedId={selectedId}
            onSelect={(u) => setSelectedId(u.unit_id)} onDoubleClick={(u) => !u.session_id && onOpenUnit(u)} showAmount={canSeeAmounts} />
          <FloorLegend />
        </div>
        {selected && (
          <UnitPanel unit={selected} details={selected.session_id ? details : null} now={now} isSuper={user?.role === 'super_admin'} canCharge={canCharge}
            onClose={() => setSelectedId(null)} onOpen={() => onOpenUnit(selected)} opening={opening === selected.unit_id}
            onAddItem={() => setOrdering(true)} onSendKitchen={sendKitchen} onPrint={onPrint}
            onSplit={() => navigate(`/cashier?unit=${selected.unit_id}`)} onCharge={() => checkout.setOpen(true)} />
        )}
      </div>
      {ordering && selected?.session_id && <OrderTaker sessionId={selected.session_id} title={selected.name} onClose={() => setOrdering(false)} onSent={() => { setOrdering(false); loadDetails(selected); }} />}
      <ModernCheckoutDialog open={checkout.open} onOpenChange={checkout.setOpen} title={selected?.name || ''} total={details?.total || 0}
        tip={checkout.tip} setTip={checkout.setTip} method={checkout.method} setMethod={checkout.setMethod} onConfirm={checkout.confirm} />
    </div>
  );
}
